import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Newspaper, Megaphone, Calendar, Image, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { getAuthHeader } = useAdminAuth();
  const [stats, setStats] = useState({
    news: 0,
    announcements: 0,
    events: 0,
    gallery: 0,
    projects: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [newsRes, announcementsRes, eventsRes, galleryRes, projectsRes] = await Promise.all([
        axios.get(`${API_URL}/api/news`),
        axios.get(`${API_URL}/api/announcements`),
        axios.get(`${API_URL}/api/events`),
        axios.get(`${API_URL}/api/gallery`),
        axios.get(`${API_URL}/api/projects`)
      ]);

      setStats({
        news: newsRes.data.length,
        announcements: announcementsRes.data.length,
        events: eventsRes.data.length,
        gallery: galleryRes.data.length,
        projects: projectsRes.data.length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'News Articles',
      count: stats.news,
      icon: Newspaper,
      color: 'bg-blue-500',
      link: '/admin/news'
    },
    {
      title: 'Projects',
      count: stats.projects,
      icon: TrendingUp,
      color: 'bg-indigo-500',
      link: '/admin/projects'
    },
    {
      title: 'Announcements',
      count: stats.announcements,
      icon: Megaphone,
      color: 'bg-yellow-500',
      link: '/admin/announcements'
    },
    {
      title: 'Events',
      count: stats.events,
      icon: Calendar,
      color: 'bg-green-500',
      link: '/admin/events'
    },
    {
      title: 'Gallery Images',
      count: stats.gallery,
      icon: Image,
      color: 'bg-purple-500',
      link: '/admin/gallery'
    }
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 text-white">
          <h1 className="text-3xl font-bold mb-2">Welcome to Admin Dashboard</h1>
          <p className="text-blue-100">Manage your Hiri Koiari DDA website content</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card) => {
            const Icon = card.icon;
            return (
              <Link
                key={card.title}
                to={card.link}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`${card.color} w-12 h-12 rounded-lg flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <TrendingUp className="w-5 h-5 text-gray-400" />
                </div>
                <h3 className="text-gray-600 text-sm font-medium mb-1">{card.title}</h3>
                <p className="text-3xl font-bold text-gray-900">{loading ? '...' : card.count}</p>
              </Link>
            );
          })}
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Link
              to="/admin/news"
              className="px-6 py-4 bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold rounded-lg text-center transition-colors"
            >
              Add News
            </Link>
            <Link
              to="/admin/projects"
              className="px-6 py-4 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold rounded-lg text-center transition-colors"
            >
              Add Project
            </Link>
            <Link
              to="/admin/announcements"
              className="px-6 py-4 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 font-semibold rounded-lg text-center transition-colors"
            >
              Add Announcement
            </Link>
            <Link
              to="/admin/events"
              className="px-6 py-4 bg-green-50 hover:bg-green-100 text-green-700 font-semibold rounded-lg text-center transition-colors"
            >
              Add Event
            </Link>
            <Link
              to="/admin/gallery"
              className="px-6 py-4 bg-purple-50 hover:bg-purple-100 text-purple-700 font-semibold rounded-lg text-center transition-colors"
            >
              Add Image
            </Link>
          </div>
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6">
          <h3 className="text-lg font-bold text-blue-900 mb-2">Getting Started</h3>
          <ul className="space-y-2 text-blue-800">
            <li>• Use the navigation menu to manage different content types</li>
            <li>• All changes are saved immediately to the database</li>
            <li>• Content will be visible on the public website instantly</li>
            <li>• Remember to use high-quality images for best results</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
